import { useEffect, useRef } from 'react';

/**
 * BlitzMap - 2-level tree of the Blitz queue.
 *
 * Two rendering modes:
 *   inline={false} (default) — fixed full-screen overlay with backdrop and
 *     close button. Used on mobile / narrow viewports.
 *   inline={true} — fills its container with no backdrop or close button.
 *     Used in the desktop sidebar; the sidebar itself is responsible for
 *     sizing and scrolling the outer container.
 *
 * In overlay mode, selecting an item closes the overlay (calls onClose).
 * In sidebar mode, onClose is a no-op and selecting just navigates.
 */
function BlitzMap({ queue, currentIndex, onSelect, onClose, inline = false }) {
  const currentItemRef = useRef(null);

  // Overlay: scroll to the active item when the overlay opens.
  useEffect(() => {
    if (inline) return;
    if (currentItemRef.current) {
      currentItemRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sidebar: keep the active item in view as playback advances.
  useEffect(() => {
    if (!inline) return;
    if (currentItemRef.current) {
      currentItemRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [inline, currentIndex]);

  const buildTree = () => {
    const chapters = [];
    let currentChapter = null;
    queue.forEach((item, index) => {
      if (item.level === 'chapter') {
        currentChapter = { ...item, index, keyPoints: [] };
        chapters.push(currentChapter);
      } else if (item.level === 'point' && currentChapter) {
        currentChapter.keyPoints.push({ ...item, index });
      }
    });
    return chapters;
  };

  const tree = buildTree();

  const handleSelect = (index) => {
    onSelect(index);
    if (!inline) onClose();
  };

  const treeContent = (
    <div className="flex-1 overflow-y-auto p-4">
      {tree.map((chapter) => {
        const chapterPlayable = !!chapter.audioUrl;
        return (
          <div key={chapter.id} className="mb-4">
            <button
              ref={currentIndex === chapter.index ? currentItemRef : null}
              onClick={chapterPlayable ? () => handleSelect(chapter.index) : undefined}
              disabled={!chapterPlayable}
              className={`w-full text-left p-2 rounded-lg transition-colors ${
                currentIndex === chapter.index
                  ? 'bg-blue-600 text-white'
                  : chapterPlayable
                    ? 'bg-gray-700 hover:bg-gray-600 text-gray-100'
                    : 'bg-gray-700/40 text-gray-400 cursor-not-allowed'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="px-1.5 py-0.5 text-xs font-medium bg-blue-500/30 text-blue-300 rounded">Chapter</span>
                <span className="font-medium truncate">{chapter.title || chapter.chapterTitle}</span>
                {!chapterPlayable && (
                  <span className="text-xs text-gray-500 italic ml-1">(summary not yet rendered)</span>
                )}
              </div>
            </button>

            {chapter.keyPoints.map((kp) => {
              const label = kp.title || kp.text || 'Key point';
              const playable = !!kp.audioUrl;
              return (
                <button
                  key={kp.id}
                  ref={currentIndex === kp.index ? currentItemRef : null}
                  onClick={playable ? () => handleSelect(kp.index) : undefined}
                  disabled={!playable}
                  className={`w-full text-left ml-4 mt-1 p-2 rounded-lg transition-colors ${
                    currentIndex === kp.index
                      ? 'bg-purple-600 text-white'
                      : playable
                        ? 'bg-gray-700/50 hover:bg-gray-600 text-gray-200'
                        : 'bg-gray-700/20 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="px-1.5 py-0.5 text-xs font-medium bg-purple-500/30 text-purple-300 rounded">Point</span>
                    <span className="text-sm truncate">{label}</span>
                    {!playable && (
                      <span className="text-xs text-gray-500 italic ml-1">(not yet rendered)</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );

  if (inline) {
    return (
      <div className="flex flex-col h-full bg-gray-800">
        <div className="px-4 py-3 border-b border-gray-700 shrink-0">
          <h2 className="text-sm font-semibold text-white">Content Map</h2>
        </div>
        {treeContent}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
      <div className="bg-gray-800 rounded-lg w-full max-w-2xl max-h-[80vh] mx-4 flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white">Content Map</h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-700 transition-colors"
          >
            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {treeContent}
      </div>
    </div>
  );
}

export default BlitzMap;
